import { ApiValidationError } from '~~/errors/ApiValidationError'
import { EAuthorizationError } from '~~/errors/EAuthorizationError'
import { ENoticeRequiredError } from '~~/errors/ENoticeRequiredError'
import { EMaintenanceWriteBlockedError } from '~~/errors/EMaintenanceWriteBlockedError'
import { EApiError } from '~~/errors/EApiError'

import illustration404Empty from '~/assets/illustrations/404-empty.svg'
import illustrationErrorGlobal from '~/assets/illustrations/error-global.svg'

/** Illustration « prise / vide / 404 / API » (fichier unique demandé métier). */
export const APP_ERROR_ILLUSTRATION_NOT_FOUND_OR_API = illustration404Empty as string
/** Illustration erreurs globales inattendues. */
const APP_ERROR_ILLUSTRATION_GLOBAL = illustrationErrorGlobal as string

type AppErrorVisualKind =
  | 'not_found'
  | 'api_unavailable'
  | 'global'
  | 'empty'
  | 'authorization'
  | 'validation'
  | 'notice'
  | 'maintenance'

export interface AppErrorScreenModel {
  visualKind: AppErrorVisualKind
  title: string
  description: string
  illustrationUrl: string
  /** Écran plein page (chargement bloqué) vs toast / inline. */
  blocking: boolean
  showBackButton: boolean
  showHomeButton: boolean
  /** Proposer déconnexion + nettoyage cookies côté client (session corrompue, boucles 401, etc.). */
  showSessionReset: boolean
}

interface FetchLike {
  status?: number
  statusCode?: number
  message?: string
  data?: { message?: string; status?: number }
  response?: { status?: number }
}

function getHttpStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object') {
    const fetchLike = error as FetchLike
    return fetchLike.status ?? fetchLike.statusCode ?? fetchLike.response?.status ?? fetchLike.data?.status
  }
  return undefined
}

/** Exposé pour le fil d’Ariane (404 direction, etc.) sans dupliquer la logique HTTP. */
export function getHttpStatusFromUnknown(error: unknown): number | undefined {
  return getHttpStatus(error)
}

function isTransientOrNetworkFailure(error: unknown): boolean {
  const status = getHttpStatus(error)
  if (status === undefined) {
    if (error instanceof TypeError) {
      return true
    }
    const messageLower = messageFromUnknown(error).toLowerCase()
    return (
      messageLower.includes('failed to fetch') ||
      messageLower.includes('networkerror') ||
      messageLower.includes('network request failed') ||
      messageLower.includes('load failed')
    )
  }
  return status === 502 || status === 503 || status === 504 || status === 408
}

function isNotFoundStatus(status: number | undefined): boolean {
  return status === 404
}

function messageFromUnknown(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const raw = (error as { message?: unknown }).message
    return typeof raw === 'string' ? raw : String(error)
  }
  return 'Une erreur est survenue.'
}

/**
 * Erreurs de chargement / navigation : affichage carte pleine page.
 */
export function classifyUnknownToScreenModel(error: unknown): AppErrorScreenModel {
  const status = getHttpStatus(error)
  const message = messageFromUnknown(error)

  if (error instanceof ApiValidationError) {
    return {
      visualKind: 'validation',
      title: 'Données invalides',
      description: error.message,
      illustrationUrl: APP_ERROR_ILLUSTRATION_GLOBAL,
      blocking: false,
      showBackButton: true,
      showHomeButton: true,
      showSessionReset: false,
    }
  }

  if (error instanceof ENoticeRequiredError) {
    return {
      visualKind: 'notice',
      title: 'Notice requise',
      description: error.message,
      illustrationUrl: APP_ERROR_ILLUSTRATION_GLOBAL,
      blocking: true,
      showBackButton: true,
      showHomeButton: true,
      showSessionReset: false,
    }
  }

  if (error instanceof EMaintenanceWriteBlockedError) {
    return {
      visualKind: 'maintenance',
      title: 'Maintenance',
      description: error.message,
      illustrationUrl: APP_ERROR_ILLUSTRATION_GLOBAL,
      blocking: false,
      showBackButton: true,
      showHomeButton: true,
      showSessionReset: false,
    }
  }

  if (error instanceof EAuthorizationError) {
    return {
      visualKind: 'authorization',
      title: 'Session ou accès',
      description: error.message,
      illustrationUrl: APP_ERROR_ILLUSTRATION_NOT_FOUND_OR_API,
      blocking: true,
      showBackButton: true,
      showHomeButton: true,
      showSessionReset: true,
    }
  }

  if (error instanceof EApiError) {
    return {
      visualKind: 'global',
      title: 'Une erreur est survenue',
      description: error.message,
      illustrationUrl: APP_ERROR_ILLUSTRATION_GLOBAL,
      blocking: true,
      showBackButton: true,
      showHomeButton: true,
      showSessionReset: false,
    }
  }

  if (isNotFoundStatus(status)) {
    return {
      visualKind: 'not_found',
      title: 'Erreur 404',
      description:
        message.trim().length > 0
          ? message
          : 'La page que vous cherchez n’existe plus ou a été déplacée.',
      illustrationUrl: APP_ERROR_ILLUSTRATION_NOT_FOUND_OR_API,
      blocking: true,
      showBackButton: true,
      showHomeButton: true,
      showSessionReset: false,
    }
  }

  if (isTransientOrNetworkFailure(error) || (status !== undefined && status >= 500)) {
    return {
      visualKind: 'api_unavailable',
      title: 'Connexion à l’API impossible',
      description:
        'Le service est momentanément indisponible ou injoignable. Vérifiez votre connexion, patientez quelques instants puis réessayez.',
      illustrationUrl: APP_ERROR_ILLUSTRATION_NOT_FOUND_OR_API,
      blocking: true,
      showBackButton: true,
      showHomeButton: true,
      showSessionReset: false,
    }
  }

  return {
    visualKind: 'global',
    title: 'Une erreur est survenue',
    description:
      message.trim().length > 0
        ? message
        : 'Un incident empêche l’affichage de cette page. Si le problème persiste, réessayez plus tard.',
    illustrationUrl: APP_ERROR_ILLUSTRATION_GLOBAL,
    blocking: true,
    showBackButton: true,
    showHomeButton: true,
    showSessionReset: false,
  }
}

/**
 * État vide métier (direction sans domaine/agence, etc.) - même illustration que 404 / connexion.
 * Pas de bouton Accueil : l’utilisateur est déjà dans sur l'app (logo, fil d’Ariane).
 */
export function emptyStateScreenModel(options: {
  title: string
  description: string
}): AppErrorScreenModel {
  return {
    visualKind: 'empty',
    title: options.title,
    description: options.description,
    illustrationUrl: APP_ERROR_ILLUSTRATION_NOT_FOUND_OR_API,
    blocking: false,
    showBackButton: true,
    showHomeButton: false,
    showSessionReset: false,
  }
}

interface NuxtLikeError {
  statusCode?: number
  statusMessage?: string
  message?: string
}

/**
 * Erreur Nuxt (`createError`, `error.vue`, `showError`).
 */
export function classifyNuxtErrorToScreenModel(error: NuxtLikeError | null | undefined): AppErrorScreenModel {
  if (!error) {
    return {
      visualKind: 'global',
      title: 'Une erreur est survenue',
      description: 'Un incident empêche l’affichage de cette page.',
      illustrationUrl: APP_ERROR_ILLUSTRATION_GLOBAL,
      blocking: true,
      showBackButton: true,
      showHomeButton: true,
      showSessionReset: false,
    }
  }

  const statusCode = error.statusCode
  const message =
    (typeof error.statusMessage === 'string' && error.statusMessage.length > 0
      ? error.statusMessage
      : undefined) ??
    (typeof error.message === 'string' && error.message.length > 0 ? error.message : undefined) ??
    'Un incident empêche l’affichage de cette page.'

  const synthetic = { status: statusCode, message } as FetchLike
  return classifyUnknownToScreenModel(synthetic)
}
